import { FC } from "react";
import { hexToString, toBytes } from "viem";

import {
  useAllowance,
  useMetamask,
  useServerSignature,
  useWriteContractWithSimulation,
} from "@dzambalaorg/react-hooks-eth";
import { type IServerSignature, TokenType } from "@dzambalaorg/types-blockchain";
import {
  convertDatabaseAssetToChainAsset,
  convertDatabaseAssetToTokenTypeAsset,
  getEthPrice,
} from "@framework/exchange";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, IContract, IMysteryBox } from "@framework/types";
import MysteryBoxPurchaseABI from "@framework/abis/json/ExchangeMysteryBoxFacet/purchaseMystery.json";
import { ZeroAddress } from "@framework/constants";

interface IMysteryBoxBuyButtonProps {
  className?: string;
  disabled?: boolean;
  mysteryBox: IMysteryBox;
  variant?: ListActionVariant;
}

export const MysteryBoxPurchaseButton: FC<IMysteryBoxBuyButtonProps> = props => {
  const { className, disabled, mysteryBox, variant = ListActionVariant.button } = props;

  const { writeContractAsync } = useWriteContractWithSimulation();

  const referrer = ZeroAddress;

  const { execute: metaFnWithAllowance } = useAllowance(async (sign: IServerSignature, systemContract: IContract) => {
    const content = convertDatabaseAssetToChainAsset(mysteryBox.content!.components);
    const price = convertDatabaseAssetToChainAsset(mysteryBox.template!.price!.components);

    return writeContractAsync({
      address: systemContract.address as AddressType,
      abi: MysteryBoxPurchaseABI,
      functionName: "purchaseMystery",
      args: [
        {
          externalId: mysteryBox.id,
          expiresAt: sign.expiresAt,
          nonce: toBytes(sign.nonce, { size: 32 }),
          extra: hexToString("0x", { size: 32 }),
          receiver: mysteryBox.template!.contract!.merchant!.wallet,
          referrer: ZeroAddress,
        },
        {
          tokenType: Object.values(TokenType).indexOf(TokenType.ERC721),
          token: mysteryBox.template!.contract!.address,
          tokenId: mysteryBox.templateId,
          amount: "1",
        },
        price,
        content,
        sign.signature,
        {
          value: getEthPrice(mysteryBox.template?.price),
        },
      ],
    });
  });

  const metaFnWithSign = useServerSignature((_values: null, sign: IServerSignature, systemContract: IContract) => {
    const price = convertDatabaseAssetToTokenTypeAsset(mysteryBox.template!.price!.components);
    return metaFnWithAllowance({ contract: systemContract.address, assets: price }, sign, systemContract);
  });

  const { execute: metaFn } = useMetamask(() => {
    return metaFnWithSign(
      {
        url: "/mystery/sign",
        method: "POST",
        data: {
          referrer,
          mysteryBoxId: mysteryBox.id,
        },
      },
      null,
    ) as Promise<void>;
  });

  const handleBuy = async () => {
    await metaFn();
  };

  return (
    <ListAction
      onClick={handleBuy}
      message="form.buttons.buy"
      className={className}
      dataTestId="MysteryBoxPurchaseButton"
      disabled={disabled}
      variant={variant}
    />
  );
};
